import { COLORS, COLOR_ORDER, FACE_CONFIG, FACE_ORDER, STICKERS_PER_FACE } from './cube-config.js';
import {
  CENTER_ORDER,
  COLOR_TO_FACE,
  CORNER_FACELETS,
  CORNER_NAMES,
  CORNER_PIECE_BY_KEY,
  EDGE_FACELETS,
  EDGE_NAMES,
  EDGE_PIECE_BY_KEY,
  FACE_TO_COLOR,
  OPPOSITE_FACE,
  pieceKey,
} from './cubie-config.js';

function error(code, message, details = {}) {
  return { code, message, details };
}

function permutationParity(permutation) {
  let inversions = 0;
  for (let i = 0; i < permutation.length; i += 1) {
    for (let j = i + 1; j < permutation.length; j += 1) {
      if (permutation[i] > permutation[j]) inversions += 1;
    }
  }
  return inversions % 2;
}

function rotateLeft(value, amount) {
  return value.slice(amount) + value.slice(0, amount);
}

function colorsLabel(faceLetters) {
  return [...faceLetters].map((face) => COLORS[FACE_TO_COLOR[face]].name.replace('色', '')).join('');
}

function containsOppositeFaces(faceLetters) {
  return [...faceLetters].some((face) => faceLetters.includes(OPPOSITE_FACE[face]));
}

function readPiece(stickers, facelets) {
  return facelets.map(([face, index]) => COLOR_TO_FACE[stickers[face][index]]).join('');
}

function validateCompleteness(stickers) {
  const errors = [];
  if (!stickers || typeof stickers !== 'object') {
    return [error('STICKER_DATA_MISSING', '没有可校验的贴纸数据')];
  }

  for (const face of FACE_ORDER) {
    if (!Array.isArray(stickers[face]) || stickers[face].length !== STICKERS_PER_FACE) {
      errors.push(error('FACE_LENGTH', `${face} 面必须恰好包含 9 张贴纸`, { face }));
    }
  }
  if (errors.length) return errors;

  const all = FACE_ORDER.flatMap((face) => stickers[face]);
  const missing = all.filter((color) => color === null || typeof color === 'undefined').length;
  if (missing) errors.push(error('MISSING_STICKERS', `仍有 ${missing} 张贴纸尚未填写`, { missing }));

  const unknown = all.filter((color) => color !== null && !COLOR_ORDER.includes(color));
  if (unknown.length) errors.push(error('UNKNOWN_COLOR', `检测到 ${unknown.length} 张未知颜色贴纸`));

  const counts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
  for (const color of all) if (color in counts) counts[color] += 1;
  for (const color of COLOR_ORDER) {
    const count = counts[color];
    if (count === STICKERS_PER_FACE) continue;
    const difference = Math.abs(STICKERS_PER_FACE - count);
    const direction = count < STICKERS_PER_FACE ? `还缺少 ${difference} 张` : `多出 ${difference} 张`;
    errors.push(error('COLOR_COUNT', `${COLORS[color].name}贴纸有 ${count} 张，${direction}`, { color, count }));
  }

  const centers = FACE_ORDER.map((face) => stickers[face][4]);
  if (new Set(centers).size !== FACE_ORDER.length) {
    errors.push(error('DUPLICATE_CENTERS', '六个中心块的颜色必须互不重复'));
  }
  for (const face of FACE_ORDER) {
    const expected = FACE_CONFIG[face].color;
    if (stickers[face][4] !== expected) {
      errors.push(error(
        'CENTER_ORIENTATION',
        `${face} 面中心块必须是${COLORS[expected].name}，请确认白顶、绿前、红右的录入方向`,
        { face, expected },
      ));
    }
  }
  return errors;
}

function reconstructCubies(stickers) {
  const errors = [];
  const edges = { pieces: [], orientation: [] };
  const corners = { pieces: [], orientation: [] };

  for (const position of EDGE_NAMES) {
    const observed = readPiece(stickers, EDGE_FACELETS[position]);
    const piece = EDGE_PIECE_BY_KEY[pieceKey(observed)];
    if (typeof piece === 'undefined') {
      const impossible = containsOppositeFaces(observed);
      errors.push(error(
        'INVALID_EDGE',
        impossible
          ? `${colorsLabel(observed)}是相对色，不能位于同一个棱块上`
          : `不存在${colorsLabel(observed)}棱块`,
        { position, observed },
      ));
      continue;
    }
    const canonical = EDGE_NAMES[piece];
    edges.pieces.push(piece);
    edges.orientation.push(observed === canonical ? 0 : 1);
  }

  for (const position of CORNER_NAMES) {
    const observed = readPiece(stickers, CORNER_FACELETS[position]);
    const piece = CORNER_PIECE_BY_KEY[pieceKey(observed)];
    if (typeof piece === 'undefined') {
      const impossible = containsOppositeFaces(observed);
      errors.push(error(
        'INVALID_CORNER',
        impossible
          ? `${colorsLabel(observed)}包含相对色，不能位于同一个角块上`
          : `不存在${colorsLabel(observed)}角块`,
        { position, observed },
      ));
      continue;
    }
    const canonical = CORNER_NAMES[piece];
    const orientation = [0, 1, 2].find((amount) => rotateLeft(canonical, amount) === observed);
    if (typeof orientation === 'undefined') {
      errors.push(error('MIRRORED_CORNER', `${colorsLabel(observed)}角块的贴纸顺序发生镜像，真实魔方无法形成`, { position, observed }));
      continue;
    }
    corners.pieces.push(piece);
    corners.orientation.push(orientation);
  }

  if (errors.length) return { errors };

  for (const [kind, names, pieces] of [
    ['角块', CORNER_NAMES, corners.pieces],
    ['棱块', EDGE_NAMES, edges.pieces],
  ]) {
    const counts = new Map();
    for (const piece of pieces) counts.set(piece, (counts.get(piece) ?? 0) + 1);
    for (let piece = 0; piece < names.length; piece += 1) {
      const count = counts.get(piece) ?? 0;
      if (count > 1) errors.push(error(`DUPLICATE_${kind === '角块' ? 'CORNER' : 'EDGE'}`, `检测到重复的${colorsLabel(names[piece])}${kind}`, { piece, count }));
      if (count === 0) errors.push(error(`MISSING_${kind === '角块' ? 'CORNER' : 'EDGE'}`, `缺少${colorsLabel(names[piece])}${kind}`, { piece }));
    }
  }

  return { errors, edges, corners };
}

export function validateCubeState(stickers) {
  const level1Errors = validateCompleteness(stickers);
  if (level1Errors.length) return { valid: false, level: 'completeness', errors: level1Errors };

  const reconstructed = reconstructCubies(stickers);
  if (reconstructed.errors.length) {
    return { valid: false, level: 'pieces', errors: reconstructed.errors };
  }

  const { edges, corners } = reconstructed;
  const reachabilityErrors = [];
  if (corners.orientation.reduce((sum, value) => sum + value, 0) % 3 !== 0) {
    reachabilityErrors.push(error('CORNER_TWIST', '角块朝向总和不合法，检测到无法由真实转动形成的角块扭转'));
  }
  if (edges.orientation.reduce((sum, value) => sum + value, 0) % 2 !== 0) {
    reachabilityErrors.push(error('EDGE_FLIP', '该状态存在单独翻转的棱块，真实三阶魔方无法形成'));
  }
  if (permutationParity(corners.pieces) !== permutationParity(edges.pieces)) {
    reachabilityErrors.push(error('PERMUTATION_PARITY', '角块与棱块的置换奇偶性不一致'));
  }
  if (reachabilityErrors.length) return { valid: false, level: 'reachability', errors: reachabilityErrors };

  const patternData = {
    EDGES: edges,
    CORNERS: corners,
    CENTERS: {
      pieces: CENTER_ORDER.map((_, index) => index),
      orientation: CENTER_ORDER.map(() => 0),
      orientationMod: CENTER_ORDER.map(() => 1),
    },
  };

  return { valid: true, level: 'valid', errors: [], patternData };
}
